package com.elan.edubridge.ui.member

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.CreateCommentRequest
import com.elan.edubridge.shared.domain.model.ContentReportTargetType
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.PostAttachment
import com.elan.edubridge.shared.domain.model.PostComment
import com.elan.edubridge.shared.domain.model.PostData
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.formatTimestamp
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import com.elan.edubridge.ui.staff.BoardPage
import com.elan.edubridge.ui.staff.ContentReportModal
import com.elan.edubridge.ui.staff.Toast
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLTextAreaElement

/** `/app/me/board` — ACTIVE 멤버가 학원 게시글·댓글을 쓰고 읽는 회원 화면. */
object MyBoardPage {
    private val scope = MainScope()

    /** 게시글 조회 실패의 "다시 시도" 버튼 id(#30). */
    private const val RETRY_ID = "member-board-retry"
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    fun render(session: SessionState) {
        scope.launch {
            val context = loadMemberContext(session)
            val academyId = context.subjectAcademyId()
            if (academyId == null) {
                MemberShell.render(context, activeTab = null, bodyHtml = emptyState("아직 연결된 학원이 없어요", "학원 초대를 수락하면 게시판을 이용할 수 있어요."))
            } else {
                val result = context.repository.postsResult(academyId)
                renderList(context, academyId, result.items, result.failure)
            }
        }
    }

    private suspend fun renderList(
        context: MemberContext,
        academyId: String,
        posts: List<PostData>,
        failure: CommandFailure?,
    ) {
        // "첫 글을 남겨 보세요"를 못 읽은 화면에 띄우면 이미 있는 대화 위에 글을 쓰게 된다(#30).
        val body = when {
            posts.isNotEmpty() -> posts.joinToString("") { listItem(it) }
            failure != null -> readErrorHtml(failure, RETRY_ID)
            else -> emptyState("등록된 게시글이 없어요", "학원 구성원의 첫 글을 남겨 보세요.")
        }
        MemberShell.render(context, activeTab = null, bodyHtml = """
            <div class="eb-page-header">
                <div>
                    <h1 class="eb-page-title">게시판</h1>
                    <p class="eb-meta">학원 운영 소식과 구성원의 이야기를 확인해 보세요.</p>
                </div>
                <button class="eb-button eb-button--primary" id="member-board-create" type="button">${icon("icon-plus", "sm")}글 작성</button>
            </div>
            <section class="eb-card">$body</section>
        """.trimIndent())
        wireRetry(RETRY_ID) { render(context.session) }
        (document.getElementById("member-board-create") as? HTMLButtonElement)?.onclick = {
            openPostEditor(context, academyId, existing = null)
            null
        }
        val buttons = document.querySelectorAll(".member-board-open")
        for (index in 0 until buttons.length) {
            val button = buttons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                button.getAttribute("data-post-id")?.let { openDetail(context, academyId, it) }
                null
            }
        }
    }

    private fun listItem(post: PostData): String = """
        <article class="eb-list-row">
            ${icon("icon-notice", "md")}
            <div>
                <button class="eb-button eb-button--link member-board-open" type="button" data-post-id="${post.id.escapeHtml()}">${post.title.escapeHtml()}</button>
                <p class="eb-meta">${(post.authorName ?: "운영자").escapeHtml()} · ${formatTimestamp(post.createdAt)} · 댓글 ${post.commentCount}개${if (post.attachments.isEmpty()) "" else " · 첨부 ${post.attachments.size}개"}</p>
            </div>
            ${icon("icon-chevron-right", "xs")}
        </article>
    """.trimIndent()

    private fun openDetail(context: MemberContext, academyId: String, postId: String) {
        scope.launch {
            val result = context.repository.postResult(academyId, postId)
            val post = result.item
            if (post == null) {
                // 삭제된 글과 못 읽은 글을 같은 문구로 알리면 네트워크가 끊겼을 때
                // 사용자가 글이 지워진 줄 안다(#30).
                Toast.error(
                    if (result.isEmptySuccess) {
                        "게시글을 찾을 수 없어요."
                    } else {
                        "게시글을 불러오지 못했어요. 잠시 후 다시 시도해 주세요."
                    },
                )
                render(context.session)
                return@launch
            }
            renderDetail(context, academyId, post, context.repository.postCommentsResult(academyId, postId).items)
        }
    }

    private suspend fun renderDetail(context: MemberContext, academyId: String, post: PostData, comments: List<PostComment>) {
        val canManage = canManagePost(context, post)
        MemberShell.render(context, activeTab = null, bodyHtml = """
            <div class="eb-page-header">
                <button class="eb-button eb-button--ghost" id="member-board-back" type="button">목록으로</button>
                ${if (canManage) """
                    <div>
                        <button class="eb-button eb-button--ghost" id="member-board-edit" type="button">수정</button>
                        ${if (BoardPage.hasAttachmentCapacity(post)) """<button class="eb-button eb-button--ghost" id="member-board-attach" type="button">파일 첨부</button>""" else ""}
                        <button class="eb-button eb-button--ghost" id="member-board-delete" type="button">삭제</button>
                    </div>
                """ else ""}
                ${if (!context.session.requiresGuardianConsent) """
                    <div>
                        <button class="eb-button eb-button--ghost" id="member-board-report-post" type="button">게시글 신고</button>
                        ${if (post.authorUid != null && post.authorUid != context.session.account?.id) """<button class="eb-button eb-button--ghost" id="member-board-report-author" type="button">작성자 신고</button>""" else ""}
                    </div>
                """ else ""}
            </div>
            <article class="eb-card" aria-labelledby="member-board-post-title">
                <h1 class="eb-page-title" id="member-board-post-title">${post.title.escapeHtml()}</h1>
                <p class="eb-meta">${(post.authorName ?: "운영자").escapeHtml()} · ${formatTimestamp(post.createdAt)}${post.updatedAt?.let { " · 수정 ${formatTimestamp(it)}" }.orEmpty()}</p>
                <div style="white-space: pre-wrap;">${post.content.escapeHtml()}</div>
                ${attachmentsHtml(post)}
            </article>
            <section class="eb-card" aria-labelledby="member-board-comments-title">
                <h2 id="member-board-comments-title">댓글 ${comments.size}개</h2>
                <form id="member-board-comment-form">
                    <div class="eb-field"><label for="member-board-comment-content">댓글</label><textarea id="member-board-comment-content" maxlength="1000" rows="3" required placeholder="게시글에 대한 의견을 남겨 보세요."></textarea></div>
                    <p class="eb-form-error" id="member-board-comment-error" role="alert" hidden></p>
                    <button class="eb-button eb-button--primary" id="member-board-comment-submit" type="submit">댓글 등록</button>
                </form>
                ${if (comments.isEmpty()) """<p class="eb-meta">첫 댓글을 남겨 보세요.</p>""" else comments.joinToString("") { commentHtml(context, it) }}
            </section>
        """.trimIndent())
        (document.getElementById("member-board-back") as? HTMLButtonElement)?.onclick = { render(context.session); null }
        (document.getElementById("member-board-edit") as? HTMLButtonElement)?.onclick = {
            openPostEditor(context, academyId, post)
            null
        }
        (document.getElementById("member-board-delete") as? HTMLButtonElement)?.onclick = {
            deletePost(context, academyId, post)
            null
        }
        (document.getElementById("member-board-attach") as? HTMLButtonElement)?.onclick = {
            BoardPage.openPostAttachmentEditor(academyId, post) { savedPost ->
                openDetail(context, academyId, savedPost.id)
            }
            null
        }
        wireAttachments(academyId, post)
        wireComments(context, academyId, post)
        wireReportActions(context, academyId, post, comments)
    }

    private fun attachmentsHtml(post: PostData): String {
        if (post.attachments.isEmpty()) return ""
        return """
            <section aria-label="첨부 파일">
                <h2>첨부 파일</h2>
                ${post.attachments.joinToString("") { attachment ->
                    """<button class="eb-button eb-button--ghost eb-button--sm member-board-attachment" type="button" data-attachment-id="${attachment.id.escapeHtml()}">${attachment.fileName.escapeHtml()} · ${formatBytes(attachment.sizeBytes)}</button>"""
                }}
            </section>
        """.trimIndent()
    }

    private fun commentHtml(context: MemberContext, comment: PostComment): String = """
        <article class="eb-list-row">
            ${icon("icon-person", "md")}
            <div>
                <strong>${(comment.authorName ?: "구성원").escapeHtml()}</strong>
                <span class="eb-meta">${formatTimestamp(comment.createdAt)}</span>
                <p style="white-space: pre-wrap;">${comment.content.escapeHtml()}</p>
            </div>
            ${if (!context.session.requiresGuardianConsent) """<button class="eb-button eb-button--ghost eb-button--sm member-board-report-comment" type="button" data-comment-id="${comment.id.escapeHtml()}">신고</button>""" else ""}
            ${if (!context.session.requiresGuardianConsent && comment.authorUid != null && comment.authorUid != context.session.account?.id) """<button class="eb-button eb-button--ghost eb-button--sm member-board-report-comment-author" type="button" data-comment-id="${comment.id.escapeHtml()}">작성자 신고</button>""" else ""}
            ${if (comment.authorUid == context.session.account?.id || canModeratePosts(context)) """<button class="eb-button eb-button--ghost eb-button--sm member-board-comment-delete" type="button" data-comment-id="${comment.id.escapeHtml()}">삭제</button>""" else ""}
        </article>
    """.trimIndent()

    private fun wireComments(context: MemberContext, academyId: String, post: PostData) {
        (document.getElementById("member-board-comment-form") as? HTMLFormElement)?.onsubmit = { event ->
            event.preventDefault()
            submitComment(context, academyId, post.id)
            null
        }
        val buttons = document.querySelectorAll(".member-board-comment-delete")
        for (index in 0 until buttons.length) {
            val button = buttons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                button.getAttribute("data-comment-id")?.let { deleteComment(context, academyId, post.id, it) }
                null
            }
        }
    }

    private fun wireReportActions(context: MemberContext, academyId: String, post: PostData, comments: List<PostComment>) {
        if (context.session.requiresGuardianConsent) return
        (document.getElementById("member-board-report-post") as? HTMLButtonElement)?.onclick = {
            ContentReportModal.open(academyId, ContentReportTargetType.POST, post.id)
            null
        }
        (document.getElementById("member-board-report-author") as? HTMLButtonElement)?.onclick = {
            post.authorUid?.let { ContentReportModal.open(academyId, ContentReportTargetType.USER, it) }
            null
        }
        val commentButtons = document.querySelectorAll(".member-board-report-comment")
        for (index in 0 until commentButtons.length) {
            val button = commentButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                comments.firstOrNull { it.id == button.getAttribute("data-comment-id") }?.let { comment ->
                    ContentReportModal.open(academyId, ContentReportTargetType.COMMENT, comment.id, post.id)
                }
                null
            }
        }
        val authorButtons = document.querySelectorAll(".member-board-report-comment-author")
        for (index in 0 until authorButtons.length) {
            val button = authorButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                comments.firstOrNull { it.id == button.getAttribute("data-comment-id") }?.authorUid?.let { authorUid ->
                    ContentReportModal.open(academyId, ContentReportTargetType.USER, authorUid)
                }
                null
            }
        }
    }

    private fun submitComment(context: MemberContext, academyId: String, postId: String) {
        val content = (document.getElementById("member-board-comment-content") as? HTMLTextAreaElement)?.value?.trim().orEmpty()
        val error = document.getElementById("member-board-comment-error") as? HTMLElement
        if (content.isEmpty()) {
            showError(error, "댓글을 입력해 주세요.")
            return
        }
        if (content.length > 1000) {
            showError(error, "댓글은 1,000자 이하로 입력해 주세요.")
            return
        }
        (document.getElementById("member-board-comment-submit") as? HTMLButtonElement)?.disabled = true
        scope.launch {
            val result = commandRepository.createComment(CreateCommentRequest(academyId, postId, content))
            if (result.isSuccess) {
                Toast.success("댓글을 등록했어요.")
                openDetail(context, academyId, postId)
            } else {
                showError(error, result.errorMessage ?: "댓글을 등록하지 못했어요. 다시 시도해 주세요.")
                (document.getElementById("member-board-comment-submit") as? HTMLButtonElement)?.disabled = false
            }
        }
    }

    private fun deleteComment(context: MemberContext, academyId: String, postId: String, commentId: String) {
        if (!window.confirm("이 댓글을 삭제할까요?")) return
        scope.launch {
            val result = commandRepository.deleteComment(academyId, postId, commentId)
            if (result.isSuccess) {
                Toast.success("댓글을 삭제했어요.")
                openDetail(context, academyId, postId)
            } else {
                Toast.error(result.errorMessage ?: "댓글을 삭제하지 못했어요. 다시 시도해 주세요.")
            }
        }
    }

    private fun openPostEditor(context: MemberContext, academyId: String, existing: PostData?) {
        BoardPage.openPostEditor(academyId, existing, allowAttachments = true) { post ->
            openDetail(context, academyId, post.id)
        }
    }

    private fun deletePost(context: MemberContext, academyId: String, post: PostData) {
        if (!window.confirm("게시글을 삭제할까요?")) return
        scope.launch {
            val result = commandRepository.deletePost(academyId, post.id)
            if (result.isSuccess) {
                Toast.success("게시글을 삭제했어요.")
                render(context.session)
            } else {
                Toast.error(result.errorMessage ?: "게시글을 삭제하지 못했어요. 다시 시도해 주세요.")
            }
        }
    }

    private fun isOwnPost(context: MemberContext, post: PostData): Boolean =
        post.authorUid != null && post.authorUid == context.session.account?.id

    private fun canManagePost(context: MemberContext, post: PostData): Boolean =
        isOwnPost(context, post) || canModeratePosts(context)

    private fun canModeratePosts(context: MemberContext): Boolean =
        !context.session.requiresGuardianConsent && context.session.activePermission.atLeast(Permission.EDITOR)

    private fun wireAttachments(academyId: String, post: PostData) {
        val buttons = document.querySelectorAll(".member-board-attachment")
        for (index in 0 until buttons.length) {
            val button = buttons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                val attachmentId = button.getAttribute("data-attachment-id")
                post.attachments.firstOrNull { it.id == attachmentId }?.let { openAttachment(academyId, post.id, it) }
                null
            }
        }
    }

    private fun openAttachment(academyId: String, postId: String, attachment: PostAttachment) {
        scope.launch {
            val ticket = commandRepository.getPostAttachmentUrl(academyId, postId, attachment.id)
            val url = ticket.downloadUrl
            if (!ticket.isSuccess || url == null) {
                Toast.error(ticket.errorMessage ?: "첨부 파일 주소를 열지 못했어요. 다시 시도해 주세요.")
                return@launch
            }
            val anchor = document.createElement("a") as HTMLAnchorElement
            anchor.href = url
            anchor.target = "_blank"
            anchor.rel = "noopener"
            document.body?.appendChild(anchor)
            anchor.click()
            anchor.remove()
        }
    }

    private fun emptyState(title: String, body: String): String = """
        <div class="eb-empty-state">${icon("icon-notice", "xl")}<h2>${title.escapeHtml()}</h2><p>${body.escapeHtml()}</p></div>
    """.trimIndent()

    private fun showError(target: HTMLElement?, message: String) {
        target?.textContent = message
        target?.removeAttribute("hidden")
    }


    private fun formatBytes(sizeBytes: Int): String = when {
        sizeBytes < 1024 -> "$sizeBytes B"
        sizeBytes < 1024 * 1024 -> "${sizeBytes / 1024} KB"
        else -> {
            val tenthsOfMegabyte = sizeBytes * 10 / (1024 * 1024)
            "${tenthsOfMegabyte / 10}.${tenthsOfMegabyte % 10} MB"
        }
    }
}
